import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    CheckCircle,
    Mic,
    Videocam,
    Stop,
    PlayArrow,
    Pause,
    Save,
    ExpandMore,
    History
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { userRequest } from '../../requestMethods';

const SpeakingPractice = ({ 
    contentType, 
    contentId, 
    onAICheck 
}) => {
    const [speakingNotes, setSpeakingNotes] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordedAudio, setRecordedAudio] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [recordingType, setRecordingType] = useState('audio'); // 'audio' or 'video'
    const [previousRecordings, setPreviousRecordings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [practiceId, setPracticeId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioPlayerRef = useRef(null);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Fetch existing practice data on mount
    const fetchPracticeData = useCallback(async () => {
        if (!contentType || !contentId) return;

        setLoading(true);
        try {
            const response = await userRequest.get(`/practice-speaking/${contentType}/${contentId}`);
            if (response.data.exists) {
                setPracticeId(response.data._id);
                setSpeakingNotes(response.data.userNotes || '');
                setPreviousRecordings(response.data.userAnswers || []);
            }
        } catch (error) {
            // 404 means no practice data yet, which is fine
            if (error.response?.status !== 404) {
                console.error('Gapirish mashqi ma\'lumotlarini olishda xatolik:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [contentType, contentId]);

    useEffect(() => {
        fetchPracticeData();
    }, [fetchPracticeData]);

    const startRecording = async () => {
        try {
            let stream;
            if (recordingType === 'video') {
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: true, 
                    audio: true 
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            } else {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            
            streamRef.current = stream;
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioUrl = URL.createObjectURL(audioBlob);
                setRecordedAudio(audioUrl);
                setIsRecording(false);
                
                // Stop all tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Yozishni boshlashda xatolik:', error);
            setSnackbar({
                open: true,
                message: 'Yozishni boshlash muvaffaqiyatsiz. Mikrofon/kamera ruxsatlarini tekshiring.',
                severity: 'error'
            });
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
        }
    };

    const playRecording = () => {
        if (recordedAudio && audioPlayerRef.current) {
            audioPlayerRef.current.play();
            setIsPlaying(true);
        }
    };

    const pauseRecording = () => {
        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleSave = async () => {
        if (!contentType || !contentId) {
            setSnackbar({
                open: true,
                message: 'Kontent ma\'lumotlari mavjud emas',
                severity: 'error'
            });
            return;
        }

        setSaving(true);
        try {
            // For now, we save a mock path since actual file upload is not implemented
            // In production, you would upload the audio blob first and get the URL
            const mockRecordingPath = recordedAudio 
                ? `recording_${Date.now()}.webm` 
                : '';

            const response = await userRequest.post('/practice-speaking', {
                contentType,
                contentId,
                userNotes: speakingNotes,
                userAnswer: mockRecordingPath
            });

            setPracticeId(response.data._id);
            setPreviousRecordings(response.data.userAnswers || []);
            
            setSnackbar({
                open: true,
                message: 'Gapirish mashqi muvaffaqiyatli saqlandi!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Gapirish mashqini saqlashda xatolik:', error);
            setSnackbar({
                open: true,
                message: 'Saqlashda xatolik yuz berdi',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAICheck = () => {
        if (onAICheck) {
            onAICheck('Speaking', { notes: speakingNotes, audioUrl: recordedAudio });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Paper 
                elevation={2} 
                sx={{ 
                    p: 3,
                    border: '2px solid',
                    borderColor: 'info.main',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px'
                }}
            >
                <CircularProgress />
            </Paper>
        );
    }

    return (
        <Paper 
            elevation={2} 
            sx={{ 
                p: 3,
                border: '2px solid',
                borderColor: 'info.main',
                borderRadius: 2,
                backgroundColor: 'background.paper'
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: 'info.main' }}>
                🎤 Gapirish Mashqi
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Audio yoki video yordamida javobingizni va tushuntirishingizni yozib oling. Ko'rib chiqish uchun yozuvingizni tinglang.
            </Typography>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                💡 Nutqingizni tartibga solish uchun maslahatlar:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 2 }}>
                • Gapirishdan oldin asosiy fikrlaringizni belgilang<br/>
                • Ishlatmoqchi bo'lgan asosiy iboralar yoki o'tish so'zlarini yozing<br/>
                • Qiyin so'zlar uchun talaffuz eslatmalarini qayd qiling<br/>
                • Kirish va xulosangizni rejalashtiring<br/>
                • Eslatmalaringizga to'xtash yoki ta'kidlash nuqtalarini qo'shing<br/>
                • G'oyalarni bog'lovchi so'zlar bilan uzluksiz bog'lash mashq qiling
            </Typography>

            <TextField
                fullWidth
                label="Bu yerga imkon qadar ko'proq eslatmalar yozing"
                multiline
                rows={8}
                value={speakingNotes}
                onChange={(e) => setSpeakingNotes(e.target.value)}
                placeholder="G'oyalaringizni, asosiy fikrlaringizni, lug'atingizni yozing yoki nutqingizni rejalang..."
                sx={{ mb: 3 }}
            />

            {/* Recording Type Selection */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Yozuv turi:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant={recordingType === 'audio' ? 'contained' : 'outlined'}
                        onClick={() => setRecordingType('audio')}
                        startIcon={<Mic />}
                        disabled={isRecording}
                    >
                        Faqat Audio
                    </Button>
                    <Button
                        variant={recordingType === 'video' ? 'contained' : 'outlined'}
                        onClick={() => setRecordingType('video')}
                        startIcon={<Videocam />}
                        disabled={isRecording}
                    >
                        Video
                    </Button>
                </Box>
            </Box>

            {/* Video Preview */}
            {recordingType === 'video' && (
                <Box sx={{ mb: 3 }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        style={{
                            width: '100%',
                            maxWidth: '500px',
                            borderRadius: '8px',
                            backgroundColor: '#000'
                        }}
                    />
                </Box>
            )}

            {/* Recording Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                {!isRecording ? (
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<Mic />}
                        onClick={startRecording}
                    >
                        Yozishni Boshlash
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<Stop />}
                        onClick={stopRecording}
                    >
                        Yozishni To'xtatish
                    </Button>
                )}

                {recordedAudio && (
                    <>
                        <audio
                            ref={audioPlayerRef}
                            src={recordedAudio}
                            onEnded={() => setIsPlaying(false)}
                            onPause={() => setIsPlaying(false)}
                            onPlay={() => setIsPlaying(true)}
                        />
                        {!isPlaying ? (
                            <Button
                                variant="outlined"
                                startIcon={<PlayArrow />}
                                onClick={playRecording}
                            >
                                Yozuvni Ijro Etish
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<Pause />}
                                onClick={pauseRecording}
                            >
                                To'xtatish
                            </Button>
                        )}
                    </>
                )}
            </Box>

            {/* Previous Recordings Section */}
            {previousRecordings.length > 0 && (
                <Accordion sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History />
                            <Typography>Oldingi yozuvlar</Typography>
                            <Chip 
                                label={previousRecordings.length} 
                                size="small" 
                                color="info" 
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Oldingi yozuvlar serverda saqlanadi. Audio yuklash imkoniyati keyingi versiyada qo'shiladi.
                        </Typography>
                        {previousRecordings.map((recording, index) => (
                            <Box 
                                key={index}
                                sx={{ 
                                    p: 2, 
                                    mb: 1, 
                                    bgcolor: 'grey.100', 
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Yozuv {previousRecordings.length - index}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    📁 {recording}
                                </Typography>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    color="info"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={saving || (!recordedAudio && !speakingNotes.trim())}
                >
                    {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                    onClick={handleAICheck}
                    disabled={!recordedAudio}
                >
                    AI Tekshiruvi
                </Button>
            </Box>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

SpeakingPractice.propTypes = {
    contentType: PropTypes.oneOf([
        'ContentShortStory', 
        'ContentLongPost', 
        'ContentVideoPost', 
        'AlgorithmicProblem'
    ]),
    contentId: PropTypes.string,
    onAICheck: PropTypes.func
};

export default SpeakingPractice;
